import { collaborateApi, useLazyGetCreatedTaskQuery } from 'api/collaborateApi';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
import { toast } from 'sonner';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { getSelectedSiteId } from '../store/siteInfoSlice';
import {
  getCreateModalOpen,
  getSelectedTaskId,
  getTasks,
  removeIsLoadingAIComment,
  setCreateModalOpen,
  setIsLoadingAIComment,
  setSelectedTaskId,
  setTasks
} from '../store/tasksSlice';
import { getUser } from '../store/userSlice';

// Update queue types
interface UpdateOperation {
  id: string;
  type: 'create' | 'update' | 'delete' | 'tags-changed';
  taskId: string;
  timestamp: number;
  execute: () => Promise<void>;
}

// Debounce map to prevent duplicate operations
interface DebounceEntry {
  operation: UpdateOperation;
  timeoutId: NodeJS.Timeout;
}

const useCurrentTasks = () => {
  const store = useStore<RootState>();

  const getCurrentTasks = useCallback(() => {
    return {
      tasks: getTasks(store.getState()),
      selectedTaskId: getSelectedTaskId(store.getState()),
    }
  }, [store]);

  return getCurrentTasks;
};

export function useRealtimeTasks() {
  const dispatch = useAppDispatch();
  const getCurrentTasks = useCurrentTasks();
  const { selectedTaskId } = getCurrentTasks();
  const siteId = useSelector(getSelectedSiteId);
  const createModalOpen = useAppSelector(getCreateModalOpen);
  const user = useAppSelector(getUser);
  const [getTask] = useLazyGetCreatedTaskQuery();

  // Queue management
  const updateQueue = useRef<UpdateOperation[]>([]);
  const isProcessing = useRef(false);
  const debounceMap = useRef<Map<string, DebounceEntry>>(new Map());

  // Track processed operations to prevent duplicates
  const processedOperations = useRef<Set<string>>(new Set());

  // Process queue sequentially
  const processQueue = useCallback(async () => {
    if (isProcessing.current || updateQueue.current.length === 0) {
      return;
    }

    isProcessing.current = true;

    try {
      while (updateQueue.current.length > 0) {
        const operation = updateQueue.current.shift();
        if (!operation) break;

        // Skip if already processed
        const operationKey = `${operation.type}-${operation.taskId}`;
        if (processedOperations.current.has(operationKey)) {
          continue;
        }

        try {
          await operation.execute();
          processedOperations.current.add(operationKey);

          // Clean up old processed operations (keep last 100)
          if (processedOperations.current.size > 100) {
            const entries = Array.from(processedOperations.current);
            entries.slice(0, entries.length - 100).forEach(entry => {
              processedOperations.current.delete(entry);
            });
          }
        } catch (error) {
          console.error(`Failed to process ${operation.type} update for task ${operation.taskId}:`, error);
          // Don't add to processed if it failed - allow retry
        }
      }
    } finally {
      isProcessing.current = false;
    }
  }, []);

  // Add update to queue with deduplication
  const queueUpdate = useCallback((operation: Omit<UpdateOperation, 'id' | 'timestamp'>) => {
    const operationKey = `${operation.type}-${operation.taskId}`;

    // Clear any existing debounce for this operation
    const existingDebounce = debounceMap.current.get(operationKey);
    if (existingDebounce) {
      clearTimeout(existingDebounce.timeoutId);
      // Remove the old operation from queue
      updateQueue.current = updateQueue.current.filter(op => op.id !== existingDebounce.operation.id);
    }

    const update: UpdateOperation = {
      ...operation,
      id: `${operationKey}-${Date.now()}`,
      timestamp: Date.now()
    };

    // For delete operations, process immediately
    if (operation.type === 'delete') {
      updateQueue.current.push(update);
      processQueue();
    } else {
      // For other operations, debounce to avoid rapid updates
      const timeoutId = setTimeout(() => {
        updateQueue.current.push(update);
        debounceMap.current.delete(operationKey);
        processQueue();
      }, 100); // 100ms debounce

      debounceMap.current.set(operationKey, {
        operation: update,
        timeoutId
      });
    }
  }, [processQueue]);

  const handleTaskCreated = useCallback(
    async (e: { id: string }) => {
      queueUpdate({
        type: 'create',
        taskId: e.id,
        execute: async () => {
          try {
            const { tasks } = getCurrentTasks();
            // Check if task already exists by task_id
            const existingTaskIndex = tasks.findIndex(t => t.task_id === e.id);
            if (existingTaskIndex > -1) {
              return;
            }

            const newTask = await getTask(e.id).unwrap();

            if (!newTask) {
              return;
            }

            // Don't add task if it's from the current user (to avoid duplicates with optimistic updates)
            if (user && newTask.latest_comment?.author?.id === user.id) {
              return;
            }

            // Check again for task_id (in case it was added while we were fetching)
            if (tasks.find(t => t.task_id === e.id)) {
              return;
            }

            // RACE CONDITION FIX: Also check by task_comment_id to catch optimistic tasks
            // that haven't been replaced yet by the replaceTask action
            if (tasks.find(t => t.task_comment_id === newTask.task_comment_id)) {
              return;
            }

            const updatedTasks = [newTask, ...tasks.filter(t => t.task_id !== e.id)];

            dispatch(setTasks(updatedTasks));

            // Invalidate cache for this site's tasks
            if (siteId) {
              dispatch(collaborateApi.util.invalidateTags([
                { type: 'CollaborateTasks', id: siteId }
              ]));
            }
          } catch (err) {
            console.error('Error fetching created task:', err);
            toast.error('Failed to fetch new task details.');
            throw err; // Re-throw to prevent marking as processed
          }
        }
      });
    },
    [dispatch, getCurrentTasks, getTask, queueUpdate, siteId, user]
  );

  const handleTaskDeleted = useCallback(
    (e: { id: string }) => {
      if (!e?.id) {
        console.warn('Invalid event data for task deletion:', e);
        return;
      }

      queueUpdate({
        type: 'delete',
        taskId: e.id,
        execute: async () => {
          const { tasks, selectedTaskId } = getCurrentTasks();
          // If the deleted task is currently open, close it
          if (selectedTaskId && selectedTaskId === e.id && createModalOpen) {
            dispatch(setSelectedTaskId(null));
            dispatch(setCreateModalOpen(false));
            toast.info('The task you were viewing has been deleted.');
          }

          // Remove the deleted task from the store
          const updatedTasks = tasks.filter(t => t.task_id !== e.id);
          dispatch(setTasks(updatedTasks));

          // Invalidate cache
          if (siteId) {
            dispatch(collaborateApi.util.invalidateTags([
              { type: 'CollaborateTasks', id: siteId }
            ]));
          }
        }
      });
    },
    [dispatch, getCurrentTasks, createModalOpen, queueUpdate, siteId]
  );

  const handleUpdateTask = useCallback(
    async (e: { id: string }) => {
      queueUpdate({
        type: 'update',
        taskId: e.id,
        execute: async () => {
          try {
            const { tasks } = getCurrentTasks();
            const updatedTask = await getTask(e.id).unwrap();

            if (!updatedTask) {
              console.warn('Updated task is null or undefined');
              return;
            }

            // Don't update task if it's from the current user (to avoid duplicates with optimistic updates)
            if (user && updatedTask.latest_comment?.author?.id === user.id) {
              return;
            }

            const existingTaskIndex = tasks.findIndex(t => t.task_id === e.id);

            if (existingTaskIndex > -1) {
              // Update existing task
              const updatedTasks = [...tasks];
              updatedTasks[existingTaskIndex] = updatedTask;
              dispatch(setTasks(updatedTasks));
            } else {
              // Task doesn't exist in current tasks, add it
              const updatedTasks = [
                updatedTask,
                ...tasks.filter(t => !t.task_id.includes('PENDING'))
              ];
              dispatch(setTasks(updatedTasks));
            }

            // Invalidate cache for specific task
            dispatch(collaborateApi.util.invalidateTags([
              { type: 'Task', id: e.id }
            ]));
          } catch (err) {
            console.error('Error fetching updated task:', err);
            toast.error('Failed to fetch updated task details.');
            throw err;
          }
        }
      });
    },
    [dispatch, getCurrentTasks, getTask, queueUpdate, user]
  );

  const handleTaskTagsChanged = useCallback(
    async (e: { task_id?: string }) => {
      if (!e.task_id) return;

      queueUpdate({
        type: 'tags-changed',
        taskId: e.task_id,
        execute: async () => {
          try {
            const { tasks } = getCurrentTasks();
            const updatedTask = await getTask(e.task_id!).unwrap();

            if (updatedTask) {
              const existingTaskIndex = tasks.findIndex(t => t.task_id === updatedTask.task_id);

              if (existingTaskIndex > -1) {
                const updatedTasks = [...tasks];
                updatedTasks[existingTaskIndex] = updatedTask;
                dispatch(setTasks(updatedTasks));
              }
            }
          } catch (err) {
            console.error('Error fetching task after tag change:', err);
            // Don't show toast for tag updates as they're less critical
            throw err;
          }
        }
      });
    },
    [dispatch, getCurrentTasks, getTask, queueUpdate]
  );

  const handleAiChatStatusChanged = useCallback((e: { task_id: string, status: 'running' | 'completed' | 'pending' | 'failed' }) => {
    switch (e.status) {
      //TODO: Remove stale tasks from the loadingAiComments
      case 'running':
        dispatch(setIsLoadingAIComment({ task_id: e.task_id, status: e.status }));
        break;
      case 'completed':
        // remove the task_id from the loadingAiComments
        dispatch(removeIsLoadingAIComment(e.task_id));
        break;
      case 'pending':
        dispatch(setIsLoadingAIComment({ task_id: e.task_id, status: e.status }));
        break;
      case 'failed':
        dispatch(removeIsLoadingAIComment(e.task_id));
        break;
    }
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    const _debounceMap = debounceMap.current;
    const _processedOperations = processedOperations.current;
    return () => {
      // Clear all pending operations
      updateQueue.current = [];
      isProcessing.current = false;

      // Clear all debounce timers
      _debounceMap.forEach(entry => {
        clearTimeout(entry.timeoutId);
      });
      _debounceMap.clear();

      // Clear processed operations
      _processedOperations.clear();
    };
  }, []);

  // Listen for task events using raw Echo API
  useEffect(() => {
    if (!siteId || !window.Echo) return;

    const taskChannelName = `sites.${siteId}.tasks`;
    const taskChannel = window.Echo.channel(taskChannelName);

    // Listen for task events
    taskChannel.listen('.TaskCreated', handleTaskCreated);
    taskChannel.listen('.TaskUpdated', handleUpdateTask);
    taskChannel.listen('.TaskDeleted', handleTaskDeleted);

    const aiTaskChatChannelName = `sites.${siteId}.ai-task-chat`;
    const aiTaskChatChannel = window.Echo.private(aiTaskChatChannelName);

    aiTaskChatChannel.listen('.AiTaskChatStatusChanged', handleAiChatStatusChanged);

    // Cleanup function
    return () => {
      taskChannel.stopListening('.TaskCreated');
      taskChannel.stopListening('.TaskUpdated');
      taskChannel.stopListening('.TaskDeleted');
      aiTaskChatChannel.stopListening('.AiTaskChatStatusChanged');
      window.Echo.leaveChannel(taskChannelName);
      window.Echo.leaveChannel(aiTaskChatChannelName);
    };
  }, [siteId, handleTaskCreated, handleUpdateTask, handleTaskDeleted, handleAiChatStatusChanged]);

  // Listen for task tag changes
  useEffect(() => {
    if (!selectedTaskId || !window.Echo) return;

    const channelName = `tasks.${selectedTaskId}.tags`;
    const channel = window.Echo.channel(channelName);

    // Listen for tag events
    channel.listen('.TaskTagCreated', handleTaskTagsChanged);
    channel.listen('.TaskTagDeleted', handleTaskTagsChanged);

    // Cleanup function
    return () => {
      channel.stopListening('.TaskTagCreated');
      channel.stopListening('.TaskTagDeleted');
      window.Echo.leaveChannel(channelName);
    };
  }, [selectedTaskId, handleTaskTagsChanged]);
}